import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
// import { encryptJSON } from '../utils/encryption';
// import { uploadJsonToIPFS, uploadFileToIPFS } from '../utils/ipfsClient';
import { uploadFileToIPFS } from '../utils/ipfsClient';
import IdentityManager from './IdentityManager';
import { loadIdentityFromStorage } from '../utils/cryptoIdentity';
import AccessKeyModal from './AccessKeyModal';
import accessKeyService from '../services/accessKeyService';

function SafetyPlan({ onEncrypt }) {
  // Initialize state, trying to load from localStorage if available
  const [incidents, setIncidents] = useState(() => {
    try {
      const savedData = localStorage.getItem('safetyPlanIncidents');
      return savedData ? JSON.parse(savedData) : [];
    } catch (e) {
      console.error('Error loading incidents from localStorage:', e);
      return [];
    }
  });
  
  const [newIncident, setNewIncident] = useState({
    date: '',
    time: '',
    location: '',
    description: '',
    witnessInfo: {
      hasWitnesses: false,
      list: []
    },
    injuries: '',
    evidenceNotes: '',
    ipfsArtifacts: [], // Array of IPFS CIDs and descriptions
    policeReport: { filed: false, reportNumber: '', officerName: '' },
    selfDocumented: true, // Default to self-documented
    identity: null // Will store the cryptographic identity
  });
  
  // Identity state
  const [currentIdentity, setCurrentIdentity] = useState(null);
  const [showIdentityManager, setShowIdentityManager] = useState(false);
  
  const [historyIncidents, setHistoryIncidents] = useState(() => {
    try {
      const savedData = localStorage.getItem('safetyPlanHistory');
      return savedData ? JSON.parse(savedData) : [];
    } catch (e) {
      console.error('Error loading history from localStorage:', e);
      return [];
    }
  });
  
  // Witness form state
  const [newWitness, setNewWitness] = useState({ name: '', contact: '', statement: '' });
  
  // IPFS artifact form state
  const [newArtifact, setNewArtifact] = useState({ cid: '', description: '', type: 'image' });
  const [uploadingArtifact, setUploadingArtifact] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Access key modal state
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  
  // Load identity on component mount
  useEffect(() => {
    const storedIdentity = loadIdentityFromStorage();
    if (storedIdentity) {
      setCurrentIdentity(storedIdentity);
      
      // Update the newIncident with the loaded identity
      setNewIncident(prev => ({
        ...prev,
        identity: {
          displayName: storedIdentity.displayName,
          ethAddress: storedIdentity.ethAddress,
          shortId: storedIdentity.shortId
        }
      }));
    }
  }, []);
  
  // Handle identity selection from IdentityManager
  const handleIdentitySelect = (identity) => {
    setCurrentIdentity(identity);
    
    // Update the newIncident with the selected identity
    setNewIncident(prev => ({
      ...prev,
      identity: {
        displayName: identity.displayName,
        ethAddress: identity.ethAddress,
        shortId: identity.shortId
      }
    }));
    
    // We'll keep the identity manager open for further management
    // Don't automatically hide it: setShowIdentityManager(false);
  };
  
  // Save incidents to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('safetyPlanIncidents', JSON.stringify(incidents));
      
      // Also update the safetyDocumentation in localStorage for sharing
      if (incidents.length > 0) {
        // Create the documentation data
        const processedIncidents = processIncidentsWithFullUrls(incidents);
        const processedHistory = incidents.length > 0 ? 
          historyIncidents.map(historySet => processIncidentsWithFullUrls(historySet)) : 
          historyIncidents;
        
        const documentationData = {
          metadata: {
            version: "2.0",
            created_at: new Date().toISOString(),
            description: "Safety documentation with enhanced IPFS access",
            ipfs_gateway: "https://ipfs.filebase.io/ipfs/"
          },
          incidents: {
            type: "array",
            current: processedIncidents,
            history: processedHistory,
            default: []
          },
          ipfs_help: {
            description: "All IPFS artifacts can be accessed through multiple gateways",
            recommendation: "The primary gateway at ipfs.filebase.io is recommended for reliability",
            format: "To access any artifact, use the format: https://ipfs.filebase.io/ipfs/[CID]"
          }
        };
        
        localStorage.setItem('safetyDocumentation', JSON.stringify(documentationData));
        console.log('Safety documentation updated in localStorage');
      }
    } catch (error) {
      console.error('Error saving incidents to localStorage:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents, historyIncidents]);
  
  // Save history to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('safetyPlanHistory', JSON.stringify(historyIncidents));
    } catch (error) {
      console.error('Error saving history to localStorage:', error);
    }
  }, [historyIncidents]);

  // Function to add a new incident
  const addIncident = () => {
    if (newIncident.date && newIncident.description) {
      const updatedHistoryIncidents = [...historyIncidents, [...incidents]];
      const updatedIncidents = [...incidents, { ...newIncident, id: Date.now() }];
      
      setHistoryIncidents(updatedHistoryIncidents);
      setIncidents(updatedIncidents);
      
      // Reset form values
      setNewIncident({
        date: '',
        time: '',
        location: '',
        description: '',
        witnessInfo: {
          hasWitnesses: false,
          list: []
        },
        injuries: '',
        evidenceNotes: '',
        ipfsArtifacts: [],
        policeReport: { filed: false, reportNumber: '', officerName: '' },
        selfDocumented: true
      });
      setNewWitness({ name: '', contact: '', statement: '' });
      setNewArtifact({ cid: '', description: '', type: 'image' });
      
      // Save data to localStorage immediately for sharing
      try {
        // Process all incidents to include full URLs
        const processedIncidents = processIncidentsWithFullUrls(updatedIncidents);
        const processedHistory = updatedIncidents.length > 0 ? 
          updatedHistoryIncidents.map(historySet => processIncidentsWithFullUrls(historySet)) : 
          updatedHistoryIncidents;
        
        const documentationData = {
          metadata: {
            version: "2.0",
            created_at: new Date().toISOString(),
            description: "Safety documentation with enhanced IPFS access",
            ipfs_gateway: "https://ipfs.filebase.io/ipfs/"
          },
          incidents: {
            type: "array",
            current: processedIncidents,
            history: processedHistory,
            default: []
          },
          ipfs_help: {
            description: "All IPFS artifacts can be accessed through multiple gateways",
            recommendation: "The primary gateway at ipfs.filebase.io is recommended for reliability",
            format: "To access any artifact, use the format: https://ipfs.filebase.io/ipfs/[CID]"
          }
        };
        
        localStorage.setItem('safetyDocumentation', JSON.stringify(documentationData));
        console.log('Safety documentation saved to localStorage after adding incident');
      } catch (error) {
        console.error('Error saving to localStorage after adding incident:', error);
      }
    }
  };

  // Function to remove an incident
  const removeIncident = (id) => {
    const updatedHistoryIncidents = [...historyIncidents, [...incidents]];
    const updatedIncidents = incidents.filter(incident => incident.id !== id);
    
    setHistoryIncidents(updatedHistoryIncidents);
    setIncidents(updatedIncidents);
    
    // Save data to localStorage immediately for sharing
    try {
      // Process all incidents to include full URLs
      const processedIncidents = processIncidentsWithFullUrls(updatedIncidents);
      const processedHistory = updatedIncidents.length > 0 ? 
        updatedHistoryIncidents.map(historySet => processIncidentsWithFullUrls(historySet)) : 
        updatedHistoryIncidents;
      
      const documentationData = {
        metadata: {
          version: "2.0",
          created_at: new Date().toISOString(),
          description: "Safety documentation with enhanced IPFS access",
          ipfs_gateway: "https://ipfs.filebase.io/ipfs/"
        },
        incidents: {
          type: "array",
          current: processedIncidents,
          history: processedHistory,
          default: []
        },
        ipfs_help: {
          description: "All IPFS artifacts can be accessed through multiple gateways",
          recommendation: "The primary gateway at ipfs.filebase.io is recommended for reliability",
          format: "To access any artifact, use the format: https://ipfs.filebase.io/ipfs/[CID]"
        }
      };
      
      localStorage.setItem('safetyDocumentation', JSON.stringify(documentationData));
      console.log('Safety documentation saved to localStorage after removing incident');
    } catch (error) {
      console.error('Error saving to localStorage after removing incident:', error);
    }
  };

  // Function to update police report info
  const updatePoliceReport = (filed) => {
    setNewIncident({
      ...newIncident,
      policeReport: {
        ...newIncident.policeReport,
        filed
      }
    });
  };
  
  // Function to toggle witness info
  const toggleWitnessInfo = (hasWitnesses) => {
    setNewIncident({
      ...newIncident,
      witnessInfo: {
        ...newIncident.witnessInfo,
        hasWitnesses
      }
    });
  };
  
  // Function to add a witness to the current incident
  const addWitness = () => {
    if (newWitness.name.trim()) {
      const updatedWitnessList = [...newIncident.witnessInfo.list, { ...newWitness, id: Date.now() }];
      setNewIncident({
        ...newIncident,
        witnessInfo: {
          ...newIncident.witnessInfo,
          list: updatedWitnessList
        }
      });
      setNewWitness({ name: '', contact: '', statement: '' });
    }
  };
  
  // Function to remove a witness
  const removeWitness = (id) => {
    const updatedWitnessList = newIncident.witnessInfo.list.filter(witness => witness.id !== id);
    setNewIncident({
      ...newIncident,
      witnessInfo: {
        ...newIncident.witnessInfo,
        list: updatedWitnessList
      }
    });
  };
  
  // Function to get a more precise file type
  const getDetailedFileType = (file) => {
    const mimeType = file.type;
    
    // Handle common file types more precisely
    if (mimeType.startsWith('image/')) {
      return 'image';
    } else if (mimeType.startsWith('video/')) {
      return 'video';
    } else if (mimeType.startsWith('audio/')) {
      return 'audio';
    } else if (mimeType === 'application/pdf') {
      return 'document';
    } else if (mimeType.includes('document') || mimeType.includes('text/plain') || 
               mimeType.includes('application/vnd.openxmlformats-officedocument') ||
               mimeType.includes('application/msword')) {
      return 'document';
    }
    
    // Default fallback based on file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
    const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'm4a'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'ppt', 'pptx'];
    
    if (imageExtensions.includes(fileExtension)) return 'image';
    if (videoExtensions.includes(fileExtension)) return 'video';
    if (audioExtensions.includes(fileExtension)) return 'audio';
    if (documentExtensions.includes(fileExtension)) return 'document';
    
    return 'other';
  };

  // Function to actually perform the IPFS upload
  const performFileUpload = async (file) => {
    try {
      setUploadingArtifact(true);
      setUploadError('');

      // Get detailed file type using our helper function
      const fileType = getDetailedFileType(file);
      console.log(`Detected file type: ${fileType} for ${file.name} (${file.type})`);

      // Upload to IPFS using the improved uploadFileToIPFS function
      const result = await uploadFileToIPFS(file);

      // Add the CID to the artifact form
      setNewArtifact({
        cid: result.path,
        description: file.name,
        type: fileType
      });

      console.log('File uploaded successfully:', result);

    } catch (error) {
      console.error('Error uploading to IPFS:', error);
      setUploadError('Failed to upload file. Please try again: ' + error.message);
    } finally {
      setUploadingArtifact(false);
    }
  };

  // Function to upload a file to IPFS and add it as an artifact
  // Checks for access key before proceeding
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if user has a valid access key in session
    if (!accessKeyService.hasStoredKey()) {
      console.log('🔑 No access key in session - showing modal');
      setPendingFile(file);
      setShowAccessKeyModal(true);
      // Clear the file input so user can select the same file again if needed
      event.target.value = '';
      return;
    }

    // Access key exists, proceed with upload
    console.log('✅ Access key found in session - proceeding with upload');
    await performFileUpload(file);
  };

  // Handler for when access key is successfully validated
  const handleAccessKeyValidated = async (key) => {
    console.log('✅ Access key validated, proceeding with upload');
    setShowAccessKeyModal(false);

    // Upload the pending file
    if (pendingFile) {
      await performFileUpload(pendingFile);
      setPendingFile(null);
    }
  };

  // Handler for when access key modal is cancelled
  const handleAccessKeyCancel = () => {
    console.log('❌ Access key entry cancelled');
    setShowAccessKeyModal(false);
    setPendingFile(null);
    setUploadError('Upload cancelled - access key required');
  };
  
  // Function to add an artifact to the current incident
  const addArtifact = () => {
    if (newArtifact.cid) {
      const updatedArtifacts = [...newIncident.ipfsArtifacts, { ...newArtifact, id: Date.now() }];
      setNewIncident({
        ...newIncident,
        ipfsArtifacts: updatedArtifacts
      });
      setNewArtifact({ cid: '', description: '', type: 'image' });
    }
  };
  
  // Function to remove an artifact
  const removeArtifact = (id) => {
    const updatedArtifacts = newIncident.ipfsArtifacts.filter(artifact => artifact.id !== id);
    setNewIncident({
      ...newIncident,
      ipfsArtifacts: updatedArtifacts
    });
  };

  // Function to add IPFS access URLs to each artifact
  const enrichArtifactsWithUrls = (artifacts) => {
    if (!artifacts || !Array.isArray(artifacts)) return [];
    
    return artifacts.map(artifact => {
      if (!artifact.cid) return artifact;
      
      // Base gateway URL
      const gatewayUrl = "https://ipfs.filebase.io/ipfs/";
      
      // Create various access methods
      return {
        ...artifact,
        ipfs_access: {
          cid: artifact.cid,
          full_url: `${gatewayUrl}${artifact.cid}`,
          gateway_url: gatewayUrl,
          ipfs_uri: `ipfs://${artifact.cid}`,
          permanent_url: `https://ipfs.io/ipfs/${artifact.cid}`,
          alternate_gateways: [
            `https://dweb.link/ipfs/${artifact.cid}`,
            `https://gateway.pinata.cloud/ipfs/${artifact.cid}`,
            `https://cloudflare-ipfs.com/ipfs/${artifact.cid}`
          ]
        }
      };
    });
  };

  // Process all incidents to enrich their artifacts with full URLs
  const processIncidentsWithFullUrls = (incidentsList) => {
    if (!incidentsList || !Array.isArray(incidentsList)) return [];
    
    return incidentsList.map(incident => {
      if (!incident.ipfsArtifacts) return incident;
      
      return {
        ...incident,
        ipfsArtifacts: enrichArtifactsWithUrls(incident.ipfsArtifacts)
      };
    });
  };

  // Function to export documentation as JSON
  const exportDocumentation = () => {
    // Process all incidents to include full URLs
    const processedIncidents = processIncidentsWithFullUrls(incidents);
    const processedHistory = incidents.length > 0 ? 
      historyIncidents.map(historySet => processIncidentsWithFullUrls(historySet)) : 
      historyIncidents;
    
    // Get the current identity if available
    const storedIdentity = loadIdentityFromStorage();
    const identityInfo = storedIdentity ? {
      display_name: storedIdentity.displayName,
      eth_address: storedIdentity.ethAddress,
      short_id: storedIdentity.shortId,
      public_key: storedIdentity.publicKey  // Include public key for verification
    } : null;
    
    const documentationData = {
      metadata: {
        version: "2.0",
        created_at: new Date().toISOString(),
        description: "Safety documentation with enhanced IPFS access",
        ipfs_gateway: "https://ipfs.filebase.io/ipfs/",
        identity: identityInfo  // Include identity information in metadata
      },
      incidents: {
        type: "array",
        current: processedIncidents,
        history: processedHistory,
        default: []
      },
      ipfs_help: {
        description: "All IPFS artifacts can be accessed through multiple gateways",
        recommendation: "The primary gateway at ipfs.filebase.io is recommended for reliability",
        format: "To access any artifact, use the format: https://ipfs.filebase.io/ipfs/[CID]"
      },
      verification: identityInfo ? {
        method: "ed25519",
        identity_type: "ethereum_compatible",
        description: "This documentation is cryptographically linked to the identity provided in metadata"
      } : null
    };
    
    // Save documentation to localStorage for access by DecentralizedSharing component
    try {
      localStorage.setItem('safetyDocumentation', JSON.stringify(documentationData));
      console.log('Safety documentation saved to local storage for sharing');
    } catch (error) {
      console.error('Error saving documentation to localStorage:', error);
    }
    
    const blob = new Blob([JSON.stringify(documentationData, null, 2)], {type: "application/json"});
    saveAs(blob, "safety_documentation.json");
    return documentationData;
  };

  // Function to encrypt and export documentation
  const encryptAndExport = () => {
    if (incidents.length === 0) {
      alert("Please add at least one incident before exporting");
      return;
    }
    
    const documentationData = exportDocumentation();
    onEncrypt(documentationData);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Safety Documentation</h2>
        <p className="safety-note">
          Document incidents to create a record for legal filings, restraining orders, or gun protection orders.
          <br/>
          <strong>Note:</strong> All information is saved locally on your device until you choose to export or encrypt it.
        </p>
      </div>
      <div className="card-body">
        {/* Cryptographic Identity Section */}
        <div className="identity-section" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Cryptographic Identity</h3>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => {
                console.log("Toggle identity manager, current state:", showIdentityManager);
                setShowIdentityManager(!showIdentityManager);
                // Force logging the new state after a short delay to see if it's being changed elsewhere
                setTimeout(() => {
                  console.log("Identity manager state after toggle:", showIdentityManager);
                }, 100);
              }}
            >
              {showIdentityManager ? 'Hide Identity Manager' : (currentIdentity ? 'Manage Identity' : 'Create Identity')}
            </button>
          </div>
          
          {currentIdentity ? (
            <div className="alert alert-success" style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>Identity:</strong> {currentIdentity.displayName}
                  <div style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.85rem', 
                    marginTop: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {currentIdentity.ethAddress}
                    <span 
                      style={{ 
                        fontSize: '0.7rem',
                        backgroundColor: '#6c5ce7',
                        color: 'white',
                        padding: '0.1rem 0.3rem',
                        borderRadius: '0.25rem',
                        cursor: 'help'
                      }}
                      title="This address is compatible with Ethereum and other EVM blockchains. It can be used for holding digital assets and interacting with blockchain networks."
                      onClick={(e) => {
                        e.stopPropagation();
                        alert(
                          "EVM Compatible Address\n\n" +
                          "This address follows the same format used by Ethereum and other EVM-compatible blockchains.\n\n" +
                          "It can be used for:\n" +
                          "• Holding digital assets (tokens, NFTs)\n" +
                          "• Interacting with smart contracts\n" +
                          "• Connecting to Web3 applications\n" +
                          "• Signing transactions on compatible networks\n\n" +
                          "Networks supporting this format include Ethereum, Polygon, Arbitrum, Optimism, Base, and many others."
                        );
                      }}
                    >
                      EVM Compatible
                    </span>
                  </div>
                </div>
                <div>
                  <span className="badge bg-success" style={{ padding: '0.5rem' }}>
                    ✓ Verifiable
                  </span>
                </div>
              </div>
              <p className="mt-2 mb-0">
                <small>This cryptographic identity will be linked to your documentation, allowing for verification of authenticity and can be used on blockchain networks.</small>
              </p>
            </div>
          ) : (
            <div className="alert alert-warning" style={{ marginTop: '0.5rem' }}>
              <p className="mb-0">
                No cryptographic identity linked. Creating an identity allows for verification of your documentation's authenticity.
                <button 
                  className="btn btn-sm btn-warning ms-2" 
                  onClick={() => setShowIdentityManager(true)}
                >
                  Create Identity
                </button>
              </p>
            </div>
          )}
          
          {/* Always render IdentityManager but toggle visibility with CSS */}
          <div 
            style={{ 
              marginTop: '1rem', 
              display: showIdentityManager ? 'block' : 'none',
              transition: 'all 0.3s ease'
            }}
          >
            <IdentityManager onIdentitySelect={handleIdentitySelect} />
          </div>
        </div>
        
        <h3>Record New Incident</h3>
        
        {/* Main incident information */}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input
              type="date"
              value={newIncident.date}
              onChange={(e) => setNewIncident({...newIncident, date: e.target.value})}
              className="form-control"
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Time</label>
            <input
              type="time"
              value={newIncident.time}
              onChange={(e) => setNewIncident({...newIncident, time: e.target.value})}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Location</label>
            <input
              type="text"
              value={newIncident.location}
              onChange={(e) => setNewIncident({...newIncident, location: e.target.value})}
              className="form-control"
              placeholder="Address or description of location"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Description of Incident *</label>
          <textarea
            value={newIncident.description}
            onChange={(e) => setNewIncident({...newIncident, description: e.target.value})}
            className="form-control"
            rows={3}
            placeholder="Describe what happened in detail"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Injuries or Harm</label>
          <textarea
            value={newIncident.injuries}
            onChange={(e) => setNewIncident({...newIncident, injuries: e.target.value})}
            className="form-control"
            rows={2}
            placeholder="Describe any physical or emotional harm"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Evidence Notes</label>
          <textarea
            value={newIncident.evidenceNotes}
            onChange={(e) => setNewIncident({...newIncident, evidenceNotes: e.target.value})}
            className="form-control"
            rows={2}
            placeholder="Notes about photos, messages, or other evidence"
          />
        </div>
        
        {/* Documentation Type Section */}
        <div className="form-group">
          <label className="form-label">Documentation Type</label>
          <div className="documentation-type-section" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input 
                  type="checkbox" 
                  checked={newIncident.selfDocumented}
                  onChange={(e) => setNewIncident({...newIncident, selfDocumented: e.target.checked})}
                />
                Self-documented incident
              </label>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input 
                  type="checkbox" 
                  checked={newIncident.witnessInfo.hasWitnesses}
                  onChange={(e) => toggleWitnessInfo(e.target.checked)}
                />
                Incident has witnesses
              </label>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input 
                  type="checkbox" 
                  checked={newIncident.policeReport.filed}
                  onChange={(e) => updatePoliceReport(e.target.checked)}
                />
                Police report was filed
              </label>
            </div>
          </div>
        </div>
        
        {/* Witness Information Section - Only shown if hasWitnesses is true */}
        {newIncident.witnessInfo.hasWitnesses && (
          <div className="witness-section" style={{ 
            backgroundColor: 'var(--gray-100)', 
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4>Witness Information</h4>
            
            {/* Add New Witness Form */}
            <div className="add-witness-form">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Witness Name</label>
                  <input
                    type="text"
                    value={newWitness.name}
                    onChange={(e) => setNewWitness({...newWitness, name: e.target.value})}
                    className="form-control"
                    placeholder="Full name of witness"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Contact Information</label>
                  <input
                    type="text"
                    value={newWitness.contact}
                    onChange={(e) => setNewWitness({...newWitness, contact: e.target.value})}
                    className="form-control"
                    placeholder="Phone, email, or address"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Witness Statement</label>
                <textarea
                  value={newWitness.statement}
                  onChange={(e) => setNewWitness({...newWitness, statement: e.target.value})}
                  className="form-control"
                  rows={2}
                  placeholder="What the witness observed or stated"
                />
              </div>
              
              <button
                type="button"
                className="btn btn-primary"
                onClick={addWitness}
                disabled={!newWitness.name.trim()}
              >
                Add Witness
              </button>
            </div>
            
            {/* Witness List */}
            {newIncident.witnessInfo.list.length > 0 && (
              <div className="witness-list" style={{ marginTop: '1rem' }}>
                <h5>Added Witnesses ({newIncident.witnessInfo.list.length})</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {newIncident.witnessInfo.list.map((witness) => (
                    <div key={witness.id} style={{ 
                      backgroundColor: 'white',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--gray-300)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <p style={{ fontWeight: '600', margin: '0 0 0.25rem 0' }}>{witness.name}</p>
                          {witness.contact && <p style={{ margin: '0 0 0.25rem 0' }}><strong>Contact:</strong> {witness.contact}</p>}
                          {witness.statement && <p style={{ margin: '0' }}><strong>Statement:</strong> {witness.statement}</p>}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeWitness(witness.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Police Report Section - Only shown if policReportFiled is true */}
        {newIncident.policeReport.filed && (
          <div className="report-details" style={{ 
            backgroundColor: 'var(--gray-100)', 
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4>Police Report Details</h4>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Report Number</label>
                <input
                  type="text"
                  value={newIncident.policeReport.reportNumber}
                  onChange={(e) => setNewIncident({
                    ...newIncident, 
                    policeReport: {
                      ...newIncident.policeReport,
                      reportNumber: e.target.value
                    }
                  })}
                  className="form-control"
                  placeholder="Police report #"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Officer Name</label>
                <input
                  type="text"
                  value={newIncident.policeReport.officerName}
                  onChange={(e) => setNewIncident({
                    ...newIncident, 
                    policeReport: {
                      ...newIncident.policeReport,
                      officerName: e.target.value
                    }
                  })}
                  className="form-control"
                  placeholder="Name of responding officer"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* IPFS Artifacts Section */}
        <div className="ipfs-artifacts-section" style={{ 
          backgroundColor: 'var(--gray-100)', 
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Section header with info tooltip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h4>Evidence Documentation (IPFS)</h4>
            <div 
              className="info-tooltip-container" 
              style={{ 
                position: 'relative', 
                display: 'inline-block', 
                cursor: 'help' 
              }}
              title="Click for more information about IPFS storage"
            >
              <button 
                className="info-btn" 
                style={{
                  border: 'none',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'help',
                  position: 'relative'
                }}
                onClick={() => alert(
                  "How IPFS Evidence Works:\n\n" +
                  "1. When you upload a file, it's stored on the InterPlanetary File System (IPFS), a decentralized storage network.\n\n" +
                  "2. Each file receives a unique Content ID (CID) that can be used to retrieve it from anywhere.\n\n" +
                  "3. Files on IPFS are immutable (cannot be changed) and timestamped, making them excellent for evidence documentation.\n\n" +
                  "4. To verify evidence, anyone with the CID can access the exact same file.\n\n" +
                  "Note: While IPFS is decentralized, remember that uploaded content is publicly accessible to anyone with the CID."
                )}
              >
                ?
              </button>
            </div>
          </div>
          
          {/* Information box with key details */}
          <div style={{ 
            backgroundColor: 'var(--info-light)', 
            borderLeft: '4px solid var(--info)', 
            padding: '0.75rem',
            borderRadius: '0 0.25rem 0.25rem 0',
            marginBottom: '1rem'
          }}>
            <h5 style={{ marginTop: 0, color: 'var(--info-dark)', fontSize: '0.95rem' }}>What is IPFS Evidence?</h5>
            <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              Upload photos, audio, or documents to create <strong>tamper-proof evidence</strong> that:
            </p>
            <ul style={{ fontSize: '0.9rem', paddingLeft: '1.5rem', marginBottom: '0.5rem' }}>
              <li>Cannot be altered or deleted once uploaded</li>
              <li>Has a timestamp proving when it was created</li>
              <li>Can be verified by legal professionals or authorities</li>
            </ul>
            <p style={{ fontSize: '0.85rem', marginBottom: '0', color: 'var(--info-dark)' }}>
              <strong>Important:</strong> Artifacts uploaded to IPFS will be publicly accessible via their CID.
            </p>
          </div>
          
          {/* Upload section with tooltips */}
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Upload File to IPFS</span>
              <span 
                style={{ 
                  fontSize: '0.8rem',
                  backgroundColor: 'var(--primary-light)',
                  color: 'white',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '0.25rem'
                }}
              >
                Step 1
              </span>
              <span 
                style={{ 
                  fontSize: '0.8rem',
                  backgroundColor: 'white',
                  color: 'var(--gray-700)',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '0.25rem',
                  border: '1px solid var(--gray-300)'
                }}
                title="Select any file type: images, videos, audio, documents, etc."
              >
                Select any file type
              </span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input 
                type="file"
                onChange={handleFileUpload}
                className="form-control"
                disabled={uploadingArtifact}
                title="Select a file to upload as evidence"
              />
              {uploadingArtifact && <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span 
                  className="spinner" 
                  style={{ 
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid var(--primary)',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                ></span>
                Uploading to IPFS...
              </span>}
            </div>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: 'var(--gray-600)' }}>
              Files are automatically uploaded to IPFS when selected. The resulting CID will appear below.
            </p>
            {uploadError && <p style={{ color: 'var(--danger)', marginTop: '0.5rem' }}>{uploadError}</p>}
          </div>
          
          {/* Artifact details section with tooltips */}
          <div className="form-group">
            <h5 style={{ 
              fontSize: '0.95rem', 
              marginBottom: '0.75rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem' 
            }}>
              <span>Artifact Details</span>
              <span 
                style={{ 
                  fontSize: '0.8rem',
                  backgroundColor: 'var(--primary-light)',
                  color: 'white',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '0.25rem'
                }}
              >
                Step 2
              </span>
            </h5>
            <div className="form-grid">
              <div>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>IPFS CID</span>
                  <span 
                    style={{ 
                      fontSize: '0.8rem',
                      backgroundColor: 'var(--gray-200)',
                      color: 'var(--gray-700)',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '0.25rem',
                      border: '1px solid var(--gray-300)'
                    }}
                    title="The Content ID (CID) is a unique identifier for your file on IPFS"
                  >
                    Auto-filled
                  </span>
                </label>
                <input
                  type="text"
                  value={newArtifact.cid}
                  onChange={(e) => setNewArtifact({...newArtifact, cid: e.target.value})}
                  className="form-control"
                  placeholder="Qm..."
                  title="The unique content identifier for your file on IPFS"
                />
                <p style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--gray-600)' }}>
                  This is auto-filled when you upload a file, or you can manually enter a CID
                </p>
              </div>
              <div>
                <label className="form-label">Description</label>
                <input
                  type="text"
                  value={newArtifact.description}
                  onChange={(e) => setNewArtifact({...newArtifact, description: e.target.value})}
                  className="form-control"
                  placeholder="Describe this evidence"
                  title="Add a clear description of what this evidence shows"
                />
                <p style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--gray-600)' }}>
                  Explain what this evidence shows or documents
                </p>
              </div>
              <div>
                <label className="form-label">Type</label>
                <select
                  value={newArtifact.type}
                  onChange={(e) => setNewArtifact({...newArtifact, type: e.target.value})}
                  className="form-control"
                  title="Select the type of media this file contains"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="document">Document</option>
                  <option value="other">Other</option>
                </select>
                <p style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--gray-600)' }}>
                  Usually auto-detected from the file
                </p>
              </div>
            </div>
            
            <button
              type="button"
              className="btn btn-primary"
              onClick={addArtifact}
              disabled={!newArtifact.cid || !newArtifact.description}
              style={{ 
                marginTop: '0.75rem', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.5rem 1rem'
              }}
              title="Click to add this artifact to your incident report"
            >
              <span 
                style={{ 
                  fontSize: '0.8rem',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '0.25rem'
                }}
              >
                Step 3
              </span>
              Add Artifact to Report
            </button>
          </div>
          
          {/* Artifact List */}
          {newIncident.ipfsArtifacts.length > 0 && (
            <div className="artifacts-list" style={{ marginTop: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <h5 style={{ margin: 0 }}>Added Artifacts ({newIncident.ipfsArtifacts.length})</h5>
                <span 
                  style={{ 
                    fontSize: '0.8rem',
                    backgroundColor: 'var(--success-light)',
                    color: 'white',
                    padding: '0.1rem 0.5rem',
                    borderRadius: '0.25rem'
                  }}
                >
                  Ready for report
                </span>
                <span
                  style={{ 
                    fontSize: '0.8rem',
                    marginLeft: 'auto',
                    backgroundColor: 'var(--gray-200)',
                    color: 'var(--gray-700)',
                    padding: '0.1rem 0.5rem',
                    borderRadius: '0.25rem',
                    border: '1px solid var(--gray-300)'
                  }}
                  title="Evidence will be included when you add this incident to your report"
                >
                  Will be saved with incident
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {newIncident.ipfsArtifacts.map((artifact) => (
                  <div key={artifact.id} style={{ 
                    backgroundColor: 'white',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--gray-300)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            backgroundColor: 'var(--primary-light)', 
                            color: 'white',
                            borderRadius: '0.25rem',
                            fontSize: '0.8rem'
                          }}>
                            {artifact.type.toUpperCase()}
                          </span>
                          <span style={{ fontWeight: '600' }}>{artifact.description}</span>
                        </div>
                        <div style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.85rem', 
                          backgroundColor: 'var(--gray-100)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem'
                        }}>
                          {artifact.cid}
                        </div>
                        {/* Preview based on artifact type */}
                        <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                          {artifact.type === 'image' && (
                            <div>
                              <img 
                                src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} 
                                alt={artifact.description}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '200px', 
                                  borderRadius: '0.25rem',
                                  display: 'block',
                                  margin: '0 auto',
                                  border: '1px solid var(--gray-300)'
                                }}
                                onError={(e) => {
                                  console.error('Image failed to load:', e);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div 
                                style={{ 
                                  display: 'none', 
                                  padding: '0.5rem',
                                  backgroundColor: 'var(--gray-100)',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.85rem',
                                  marginTop: '0.5rem',
                                  textAlign: 'center'
                                }}
                              >
                                Image failed to load. Please try viewing on IPFS directly.
                              </div>
                            </div>
                          )}
                          {artifact.type === 'video' && (
                            <div>
                              <video 
                                controls
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '200px', 
                                  borderRadius: '0.25rem',
                                  display: 'block',
                                  margin: '0 auto',
                                  border: '1px solid var(--gray-300)'
                                }}
                                onError={(e) => {
                                  console.error('Video failed to load:', e);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              >
                                <source src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} />
                                Your browser does not support the video tag.
                              </video>
                              <div 
                                style={{ 
                                  display: 'none', 
                                  padding: '0.5rem',
                                  backgroundColor: 'var(--gray-100)',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.85rem',
                                  marginTop: '0.5rem',
                                  textAlign: 'center'
                                }}
                              >
                                Video failed to load. Please try viewing on IPFS directly.
                              </div>
                            </div>
                          )}
                          {artifact.type === 'audio' && (
                            <div>
                              <audio 
                                controls
                                style={{ width: '100%' }}
                                onError={(e) => {
                                  console.error('Audio failed to load:', e);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              >
                                <source src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} />
                                Your browser does not support the audio tag.
                              </audio>
                              <div 
                                style={{ 
                                  display: 'none', 
                                  padding: '0.5rem',
                                  backgroundColor: 'var(--gray-100)',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.85rem',
                                  marginTop: '0.5rem',
                                  textAlign: 'center'
                                }}
                              >
                                Audio failed to load. Please try viewing on IPFS directly.
                              </div>
                            </div>
                          )}
                          {artifact.type === 'document' && (
                            <div style={{ 
                              padding: '0.5rem', 
                              backgroundColor: 'var(--gray-100)', 
                              borderRadius: '0.25rem',
                              fontSize: '0.85rem',
                              textAlign: 'center',
                              border: '1px solid var(--gray-300)'
                            }}>
                              <span className="material-icons" style={{ fontSize: '1.5rem', display: 'block', margin: '0 auto 0.5rem auto' }}>description</span>
                              Document preview not available
                            </div>
                          )}
                          {artifact.type === 'other' && (
                            <div style={{ 
                              padding: '0.5rem', 
                              backgroundColor: 'var(--gray-100)', 
                              borderRadius: '0.25rem',
                              fontSize: '0.85rem',
                              textAlign: 'center',
                              border: '1px solid var(--gray-300)'
                            }}>
                              <span className="material-icons" style={{ fontSize: '1.5rem', display: 'block', margin: '0 auto 0.5rem auto' }}>insert_drive_file</span>
                              Preview not available for this file type
                            </div>
                          )}
                        </div>
                        
                        <a 
                          href={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.85rem', marginTop: '0.5rem', display: 'inline-block' }}
                        >
                          View on IPFS
                        </a>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeArtifact(artifact.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <button 
            className="btn btn-primary"
            onClick={addIncident}
            disabled={!newIncident.date || !newIncident.description}
          >
            Add Incident
          </button>
        </div>
        
        {/* Incidents List */}
        <div className="incidents-list">
          <h3>Recorded Incidents ({incidents.length})</h3>
          
          {incidents.length === 0 ? (
            <p className="no-incidents">No incidents recorded yet</p>
          ) : (
            <div className="incidents-container">
              {incidents.map((incident, index) => (
                <div key={incident.id} className="incident-item">
                  <div className="incident-header">
                    <h4>Incident {index + 1} - {incident.date}</h4>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => removeIncident(incident.id)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="incident-details">
                    {/* Documentation Type Tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      {incident.identity && (
                        <span style={{ 
                          backgroundColor: '#28a745',  
                          color: 'white',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <span>✓</span> Cryptographically Verified
                        </span>
                      )}
                      {incident.selfDocumented && (
                        <span style={{ 
                          backgroundColor: 'var(--primary-light)', 
                          color: 'white',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem'
                        }}>
                          Self-Documented
                        </span>
                      )}
                      {incident.witnessInfo.hasWitnesses && (
                        <span style={{ 
                          backgroundColor: 'var(--teal-light)', 
                          color: 'white',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem'
                        }}>
                          Witness Documentation
                        </span>
                      )}
                      {incident.policeReport.filed && (
                        <span style={{ 
                          backgroundColor: 'var(--purple-light)', 
                          color: 'white',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem'
                        }}>
                          Police Report
                        </span>
                      )}
                      {incident.ipfsArtifacts.length > 0 && (
                        <span style={{ 
                          backgroundColor: 'var(--safe-green)', 
                          color: 'white',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem'
                        }}>
                          IPFS Evidence ({incident.ipfsArtifacts.length})
                        </span>
                      )}
                    </div>
                    
                    {/* Identity information if available */}
                    {incident.identity && (
                      <div 
                        style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '0.75rem', 
                          borderRadius: '0.25rem',
                          marginBottom: '0.75rem',
                          border: '1px solid #dee2e6' 
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong>Documented by:</strong> {incident.identity.displayName}
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.85rem', 
                              color: '#6c757d',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              {incident.identity.ethAddress}
                              <span 
                                style={{ 
                                  fontSize: '0.7rem',
                                  backgroundColor: '#6c5ce7',
                                  color: 'white',
                                  padding: '0.1rem 0.3rem',
                                  borderRadius: '0.25rem',
                                  cursor: 'help'
                                }}
                                title="This address is compatible with Ethereum and other EVM blockchains. It can be used for holding digital assets and interacting with blockchain networks."
                                onClick={(e) => {
                                  e.stopPropagation();
                                  alert(
                                    "EVM Compatible Address\n\n" +
                                    "This address follows the same format used by Ethereum and other EVM-compatible blockchains.\n\n" +
                                    "It can be used for:\n" +
                                    "• Holding digital assets (tokens, NFTs)\n" +
                                    "• Interacting with smart contracts\n" +
                                    "• Connecting to Web3 applications\n" +
                                    "• Signing transactions on compatible networks\n\n" +
                                    "Networks supporting this format include Ethereum, Polygon, Arbitrum, Optimism, Base, and many others."
                                  );
                                }}
                              >
                                EVM
                              </span>
                            </div>
                          </div>
                          <span 
                            className="badge bg-success" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            Verified Identity
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <p><strong>Time:</strong> {incident.time || 'Not specified'}</p>
                    <p><strong>Location:</strong> {incident.location || 'Not specified'}</p>
                    <p><strong>Description:</strong> {incident.description}</p>
                    {incident.injuries && <p><strong>Injuries/Harm:</strong> {incident.injuries}</p>}
                    {incident.evidenceNotes && <p><strong>Evidence Notes:</strong> {incident.evidenceNotes}</p>}
                    
                    {/* Witnesses */}
                    {incident.witnessInfo.hasWitnesses && incident.witnessInfo.list.length > 0 && (
                      <div style={{ margin: '0.75rem 0' }}>
                        <p><strong>Witnesses:</strong></p>
                        <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
                          {incident.witnessInfo.list.map((witness) => (
                            <li key={witness.id} style={{ marginBottom: '0.5rem' }}>
                              <strong>{witness.name}</strong>
                              {witness.contact && <span> ({witness.contact})</span>}
                              {witness.statement && <p style={{ margin: '0.25rem 0 0 0', fontStyle: 'italic' }}>"{witness.statement}"</p>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Police Report */}
                    {incident.policeReport.filed && (
                      <p>
                        <strong>Police Report:</strong> Yes
                        {incident.policeReport.reportNumber && <> (Report #: {incident.policeReport.reportNumber})</>}
                        {incident.policeReport.officerName && <>, Officer: {incident.policeReport.officerName}</>}
                      </p>
                    )}
                    
                    {/* IPFS Artifacts */}
                    {incident.ipfsArtifacts.length > 0 && (
                      <div style={{ margin: '0.75rem 0' }}>
                        <p><strong>IPFS Evidence:</strong></p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {incident.ipfsArtifacts.map((artifact) => (
                            <div key={artifact.id} style={{ 
                              backgroundColor: 'var(--gray-100)', 
                              padding: '0.5rem',
                              borderRadius: '0.5rem',
                              maxWidth: 'calc(50% - 0.5rem)',
                              overflow: 'hidden'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <span style={{ 
                                  backgroundColor: 'var(--primary-light)', 
                                  color: 'white',
                                  padding: '0.1rem 0.25rem',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.7rem'
                                }}>
                                  {artifact.type}
                                </span>
                                <span style={{ fontWeight: '600', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {artifact.description}
                                </span>
                              </div>
                              {/* Preview based on artifact type */}
                              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                {artifact.type === 'image' && (
                                  <div>
                                    <img 
                                      src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} 
                                      alt={artifact.description}
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '100px', 
                                        borderRadius: '0.25rem',
                                        display: 'block',
                                        margin: '0 auto',
                                        border: '1px solid var(--gray-300)'
                                      }}
                                      onError={(e) => {
                                        console.error('Image failed to load:', e);
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                    <div 
                                      style={{ 
                                        display: 'none', 
                                        padding: '0.25rem',
                                        backgroundColor: 'var(--gray-100)',
                                        borderRadius: '0.25rem',
                                        fontSize: '0.75rem',
                                        marginTop: '0.25rem',
                                        textAlign: 'center'
                                      }}
                                    >
                                      Image unavailable
                                    </div>
                                  </div>
                                )}
                                {artifact.type === 'video' && (
                                  <div>
                                    <video 
                                      controls
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '100px', 
                                        borderRadius: '0.25rem',
                                        display: 'block',
                                        margin: '0 auto',
                                        border: '1px solid var(--gray-300)'
                                      }}
                                      onError={(e) => {
                                        console.error('Video failed to load:', e);
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    >
                                      <source src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} />
                                      Your browser does not support the video tag.
                                    </video>
                                    <div 
                                      style={{ 
                                        display: 'none', 
                                        padding: '0.25rem',
                                        backgroundColor: 'var(--gray-100)',
                                        borderRadius: '0.25rem',
                                        fontSize: '0.75rem',
                                        marginTop: '0.25rem',
                                        textAlign: 'center'
                                      }}
                                    >
                                      Video unavailable
                                    </div>
                                  </div>
                                )}
                                {artifact.type === 'audio' && (
                                  <div>
                                    <audio 
                                      controls
                                      style={{ width: '100%' }}
                                      onError={(e) => {
                                        console.error('Audio failed to load:', e);
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    >
                                      <source src={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} />
                                      Your browser does not support the audio tag.
                                    </audio>
                                    <div 
                                      style={{ 
                                        display: 'none', 
                                        padding: '0.25rem',
                                        backgroundColor: 'var(--gray-100)',
                                        borderRadius: '0.25rem',
                                        fontSize: '0.75rem',
                                        marginTop: '0.25rem',
                                        textAlign: 'center'
                                      }}
                                    >
                                      Audio unavailable
                                    </div>
                                  </div>
                                )}
                                {(artifact.type !== 'image' && artifact.type !== 'video' && artifact.type !== 'audio') && (
                                  <div style={{ 
                                    padding: '0.25rem', 
                                    backgroundColor: 'var(--gray-100)', 
                                    borderRadius: '0.25rem',
                                    fontSize: '0.75rem',
                                    textAlign: 'center',
                                    border: '1px solid var(--gray-300)'
                                  }}>
                                    {artifact.type === 'document' ? 'Document' : 'File'} preview unavailable
                                  </div>
                                )}
                              </div>
                              
                              <a 
                                href={`https://ipfs.filebase.io/ipfs/${artifact.cid}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ fontSize: '0.8rem' }}
                              >
                                View on IPFS
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {incidents.length > 0 && (
          <div className="export-options">
            <button 
              onClick={exportDocumentation} 
              className="btn btn-secondary"
            >
              Export Documentation
            </button>
            <button 
              onClick={encryptAndExport} 
              className="btn btn-primary"
            >
              Encrypt & Secure Documentation
            </button>
          </div>
        )}
      </div>

      {/* Access Key Modal - Shows when user tries to upload without a key */}
      <AccessKeyModal
        isOpen={showAccessKeyModal}
        onValidKey={handleAccessKeyValidated}
        onCancel={handleAccessKeyCancel}
      />
    </div>
  );
}

export default SafetyPlan;